import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO helper: marks the current route as non-indexable for search engines.
 * Used on authenticated/internal pages (login wall, dashboard, admin, etc.).
 * Public marketing/info pages must NOT render this component.
 */
export const NoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex,nofollow" />
  </Helmet>
);

/**
 * Hook variant for pages with multiple early returns where rendering
 * <NoIndex /> in every branch would be tedious. Imperatively manages
 * a single `<meta name="robots">` tag on document.head while mounted.
 */
export const useNoIndex = () => {
  useEffect(() => {
    const META_NAME = "robots";
    const existing = document.head.querySelector<HTMLMetaElement>(
      `meta[name="${META_NAME}"]`
    );
    const previousContent = existing?.getAttribute("content") ?? null;
    const ownedExisting = !!existing;

    const tag = existing ?? document.createElement("meta");
    tag.setAttribute("name", META_NAME);
    tag.setAttribute("content", "noindex,nofollow");
    tag.setAttribute("data-noindex-hook", "1");
    if (!ownedExisting) document.head.appendChild(tag);

    return () => {
      if (ownedExisting && previousContent !== null) {
        tag.setAttribute("content", previousContent);
        tag.removeAttribute("data-noindex-hook");
      } else if (tag.parentNode) {
        tag.parentNode.removeChild(tag);
      }
    };
  }, []);
};

export default NoIndex;
